import React, { useEffect, useState } from "react";
import { getFlows, deleteFlow, saveFlow } from "../../../hooks/flowApi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FiTrash2,
  FiCopy,
  FiEye,
  FiPlus,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiEdit3,
  FiFilter,
  FiX,
} from "react-icons/fi";

export default function WorkflowManager() {
  const [flows, setFlows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();
  
  const tenantName = localStorage.getItem("tenantName");

  useEffect(() => {
    fetchFlows();
  }, []);

  const fetchFlows = async () => {
    try {
      setIsLoading(true);
      const { data } = await getFlows();
      setFlows(data);
    } catch (err) {
      console.error("Error fetching flows:", err);
      toast.error("Failed to load workflows");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;

    try {
      await deleteFlow(id);
      setFlows((prev) => prev.filter((f) => f._id !== id));
      toast.success("Workflow deleted successfully");
    } catch (err) {
      console.error("Delete failed", err);
      toast.error("Failed to delete workflow");
    }
  };

  const handleView = (id) => {
    navigate(`/${tenantName}/bot-workflow?id=${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/${tenantName}/bot-workflow?edit=${id}`);
  };

  const handleCreateNew = () => navigate(`/${tenantName}/bot-workflow`);

  const handleDuplicate = async (flow) => {
    try {
      const newFlow = {
        ...flow,
        name: `${flow.name} (Copy)`,
        nodes: flow.nodes.map((n) => ({
          ...n,
          id: `${n.id}-copy-${Date.now()}`,
        })),
        edges: flow.edges.map((e) => ({
          ...e,
          id: `${e.id}-copy-${Date.now()}`,
        })),
      };
      await saveFlow(newFlow);
      fetchFlows();
      toast.success("Workflow duplicated successfully");
    } catch (err) {
      console.error("Duplicate failed", err);
      toast.error("Failed to duplicate workflow");
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedFlows = [...flows].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  const filteredFlows = sortedFlows.filter((flow) => {
    const matchesSearch =
      flow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (flow.description &&
        flow.description.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus =
      statusFilter === "all" || flow.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { class: "status-active", text: "Active" },
      draft: { class: "status-draft", text: "Draft" },
      archived: { class: "status-archived", text: "Archived" },
    };

    const config = statusConfig[status] || {
      class: "status-default",
      text: status || "Unknown",
    };

    return (
      <span className={`status-badge ${config.class}`}>{config.text}</span>
    );
  };

  return (
    <div className="workflow-manager">
      <div className="manager-header">
        <div className="header-title-section">
          <h1>Workflow Manager</h1>
          <p>Create, manage, and organize your automation workflows</p>
        </div>
        <div className="header-actions">
          <div className="search-bar">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search workflows by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="clear-search"
                onClick={() => setSearchTerm("")}
              >
                <FiX />
              </button>
            )}
          </div>
          <div className="filter-section">
            <button
              className={`btn-filter ${showFilters ? "active" : ""}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FiFilter />
              <span>Filters</span>
            </button>
            {showFilters && (
              <div className="filter-dropdown">
                <label>Status:</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            )}
          </div>
          <button className="btn-create" onClick={handleCreateNew}>
            <FiPlus />
            <span>New Workflow</span>
          </button>
        </div>
      </div>

      <div className="table-container">
        {isLoading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading workflows...</p>
          </div>
        ) : (
          <>
            <div className="table-header">
              <table>
                <thead>
                  <tr>
                    <th className="col-name">Name</th>
                    <th className="col-description">Description</th>
                    <th className="col-status">Status</th>
                    <th className="col-created">Created</th>
                    <th className="col-updated">Last Updated</th>
                    <th className="col-actions">Actions</th>
                  </tr>
                </thead>
              </table>
            </div>
            <div className="table-body-container">
              {filteredFlows.length === 0 ? (
                <div className="empty-state">
                  <FiSearch size={48} />
                  <h3>No workflows found</h3>
                  <p>Try adjusting your search or filter criteria</p>
                  <button className="btn-create" onClick={handleCreateNew}>
                    <FiPlus />
                    <span>Create New Workflow</span>
                  </button>
                </div>
              ) : (
                <table>
                  <tbody>
                    {filteredFlows.map((flow) => (
                      <tr key={flow._id} className="workflow-row">
                        <td className="col-name">
                          <div className="flow-name">{flow.name}</div>
                        </td>
                        <td className="col-description">
                          <div className="flow-description">
                            {flow.description || "No description provided"}
                          </div>
                        </td>
                        <td className="col-status">{getStatusBadge(flow.status)}</td>
                        <td className="col-created">
                          <div className="date-cell">
                            <div className="date-value">
                              {new Date(flow.createdAt).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                }
                              )}
                            </div>
                            <div className="time-value">
                              {new Date(flow.createdAt).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="col-updated">
                          <div className="date-cell">
                            <div className="date-value">
                              {new Date(
                                flow.updatedAt || flow.createdAt
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </div>
                            <div className="time-value">
                              {new Date(
                                flow.updatedAt || flow.createdAt
                              ).toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </div>
                          </div>
                        </td>
                        <td className="col-actions">
                          <div className="action-buttons">
                            <button
                              className="btn-action btn-view"
                              onClick={() => handleView(flow._id)}
                              title="View"
                            >
                              <FiEye />
                            </button>
                            <button
                              className="btn-action btn-edit"
                              onClick={() => handleEdit(flow._id)}
                              title="Edit"
                            >
                              <FiEdit3 />
                            </button>
                            <button
                              className="btn-action btn-duplicate"
                              onClick={() => handleDuplicate(flow)}
                              title="Duplicate"
                            >
                              <FiCopy />
                            </button>
                            <button
                              className="btn-action btn-delete"
                              onClick={() => handleDelete(flow._id, flow.name)}
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}